/**
 * Life4Earth V2 — Personalized feed API adapter (DEV-1330, epic DEV-1329).
 *
 * Fan-in-on-read: GET /api/feed/{userGuid} returns a recency-ranked union of
 * the user's followed/belonged sources (news_items + activity_events),
 * normalized to one FeedItem envelope. Ranking is transparent, never
 * engagement-optimized.
 */

import { apiFetch } from "./client";

export type FeedCategory =
  | "update"      // syndicated project/mini status report
  | "article"     // shared article / chronicle article
  | "activity"    // created / joined / published
  | "celebration" // medal earned, milestone reached
  | "social"      // personal post (future, DEV-1350)
  | "suggested";  // discovery injection

export type FeedPillar = "problem" | "solution" | "enabler" | "community" | "project";

export interface FeedItem {
  id: string;                 // "news:{guid}" | "activity:{guid}"
  source: "news" | "activity";
  category: FeedCategory;
  pillar: FeedPillar;         // colors the left accent (problem=red … project=grey)
  title: string;
  body?: string | null;
  imageUrl?: string | null;   // raw — resolve via mediaSrc()
  embedHtml?: string | null;  // render (sanitized) when embedType === 'video'
  embedType?: string | null;
  sourceName?: string | null;
  href?: string | null;
  sourceEntityType?: string | null;   // community | project — the pillar it came from
  sourceEntityGuid?: string | null;
  sourceEntityName?: string | null;
  sourceEntityHref?: string | null;
  sourceEntitySurface?: string | null; // news | feed (community surface)
  authorType?: string | null;          // user | community | publisher
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  authorHref?: string | null;
  actorName?: string | null;
  actorType?: string | null;
  actorGuid?: string | null;
  verb?: string | null;
  objectType?: string | null;
  objectGuid?: string | null;
  /** The underlying news_item guid for shared posts — the delete target. */
  postGuid?: string | null;
  /** True when the viewer authored this post and may delete it everywhere. */
  canDelete?: boolean;
  timestamp: string;          // ISO
}

export interface FeedCategoryCount {
  category: string;
  count: number;
}

export interface FeedResponse {
  items: FeedItem[];
  offset: number;
  limit: number;
  hasMore: boolean;
  categoryCounts: FeedCategoryCount[];
}

export interface FetchFeedOptions {
  limit?: number;
  offset?: number;
  /** Include-filter: only these categories are returned. Omit for all. */
  categories?: FeedCategory[];
}

/** Fetch the personalized feed for a user. */
export function fetchFeed(userGuid: string, opts?: FetchFeedOptions): Promise<FeedResponse> {
  const p = new URLSearchParams();
  if (opts?.limit != null) p.set("limit", String(opts.limit));
  if (opts?.offset != null) p.set("offset", String(opts.offset));
  if (opts?.categories?.length) p.set("categories", opts.categories.join(","));
  const qs = p.toString();
  return apiFetch<FeedResponse>(`/api/feed/${userGuid}${qs ? `?${qs}` : ""}`);
}
